import { FormConfig, FormValues } from '@topdanmark-onlinesolutions/claim-form-types';
import { getPageNext } from './getPageNext';
import { hasConditionalPages } from './conditional-pages';
import { isFormApiEnabled } from './isFormApiEnabled';
import { getPagesFromApi } from '../services/claimFormApi';

export const getPageInfo = (
    formConfig: FormConfig,
    formValues: FormValues,
    pageIdCur: string,
    resetForm: () => void
) => {
    const { formId, pages } = formConfig;

    const currentPageIndex = formConfig.pages.findIndex((page) => page.pageId === pageIdCur);

    if (currentPageIndex === undefined) {
        throw new Error('Unknown pageIdCur: ' + pageIdCur);
    }

    // The user has switched forms
    // pageIdCur comes from the *state*
    // Eg 'ansoegning'
    // The formConfig has the pages from the form from the formId from the URL
    // It will probably not have the one from the state!
    if (currentPageIndex === -1) {
        resetForm();

        console.error('Could not extract currentPageIndex - The user might have switched forms');

        window.location.href = '/' + formId;
    }

    const isFirstPage = currentPageIndex === 0;
    const isLastPage = currentPageIndex === formConfig.pages.length - 1;
    const isSecondLastPage = currentPageIndex === formConfig.pages.length - 2;

    const hasNextPage = !isLastPage;
    const hasPrevPage = !isFirstPage;

    let pageIdPrev = undefined;
    if (hasPrevPage) {
        const pagePrev = pages[currentPageIndex - 1];

        pageIdPrev = pagePrev.pageId;
    }

    return {
        currentPageType: pages[currentPageIndex].pageType,
        hasNextPage,
        hasPrevPage,
        urlPrev: pageIdPrev ? `/${formId}/${pageIdPrev}` : undefined,
        urlResume: `/${formId}/resume`,
        isLastPage,
        isSecondLastPage,
        isFirstPage,
        currentPageIndex
    };
};

export const getArrayOfPagesShown = async (
    formConfig: FormConfig | undefined,
    formValues: FormValues
): Promise<string[]> => {
    if (formConfig === undefined) {
        throw new Error('getArrayOfPagesShown was called with an undefined formConfig');
    }

    // Check if the form is API enabled. If it is, fetch the pages from the Claim Form API
    // instead of using the local formConfig/conditional page.
    if (isFormApiEnabled(formConfig.formId)) {
        const pages = await getPagesFromApi(formConfig.formId, formValues);
        return pages;
    }

    // Return all pages from the form
    // if there are no conditions
    if (!hasConditionalPages(formConfig.formId)) {
        const pageArray = formConfig.pages.map((page) => page.pageId);
        return pageArray;
    }

    // Get the first page to start the processing
    let prevPage = formConfig.pages[0].pageId;

    const pages: string[] = [];
    pages.push(prevPage);

    let i = 0;
    while (i++ < formConfig.pages.length) {
        const nextPage = await getPageNext(formConfig, formValues, prevPage);

        if (nextPage === undefined) {
            break;
        }

        pages.push(nextPage);

        prevPage = nextPage;
    }

    return pages;
};
